// Deep-file import: the barrel drags selector modules into this load order,
// and those build reselect selectors at module scope.
// eslint-disable-next-line webflow/module-top-level-imports
import {selectLibraryPluginIds} from '@packages/systems/libraries/libraryPluginIds';
import {isEqual} from 'lodash';
import {isLeaf} from '@packages/systems/constraints';
import {
  EElement,
  EList,
  ELiteral,
  EText,
  type ExpressionElement,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {
  Component,
  ExpressionPath,
  Expressions,
  matchExpression,
  Plugins,
} from '@packages/systems/wfdl';
import {reportBug} from '@packages/utilities/reporter';
import {selectAllCmsRtfComponentIds} from '../../selectors/selectAllCmsRtfComponentIds';
import {
  isUserComponent,
  isUserComponentInst,
  resetFormElementsInTreeToDefaultSource,
  setPastedGridItemStyleForElement,
  getTabAttr,
  tabsPair,
  generateUniqueTabNameFromWrapper,
  replaceTabPairData,
  getBody,
  findChild,
  updateComponentRenderInComponentMap,
  generateNewTabsLinkExpression,
  generateNewTabsPaneExpression,
  getTargetedSlotAnchorPath,
  resolveValidationSlotAnchorPath,
  addElementAtAnchorInPath,
  updateXscpStyleBlockIds,
  isOptimizationElement,
  getAllComponentInstances,
  type PasteElementDetail,
  getRelativePath,
} from '@packages/utilities/platform';
import {
  isSliderMask,
  isNavbarWrapper,
  isFormInputWrapper,
  isFormSelect,
  isGridRow,
  isFormWrapper,
  isPredefinedComponent,
  isTextHolder,
  isLightboxWrapper,
  isCell,
  isTabsWrapper,
  isTabsPair,
  isTabsContent,
  isTabsMenu,
  isTabsLinkElement,
  isTabsPane,
  isImageElement,
} from '@packages/utilities/elementTypeUtils';
import {
  Qualified,
  qualify,
  typeName,
  getName,
  getPluginName,
} from '@packages/systems/wfdl-Qualified';
import {updateAssetIdsInExpression} from '@packages/systems/libraries';
import {
  concat,
  optionToBool,
  identity,
  pipe,
  map,
  compose,
  okToOption,
  extractArray,
  emptyArray,
  noneToErr,
  constant,
  traverseResults,
  constantFalse,
} from '@packages/utilities/fp/utils';
import type {
  FileRecord,
  FileRecordProps,
} from '@packages/systems/core/records/FileRecord';
import {parse} from '@packages/utilities/json';
import {
  CONTEXT_MENU_DUPLICATE,
  PASTE,
  XSCP_ASSET_IMPORT_SUCCEEDED,
} from '@packages/systems/designer/actions';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import type {
  DesignerAction,
  DesignerActionFromCreator,
} from '@packages/systems/designer/flux';
import {
  findByFileHash,
  getXscpImportedFiles,
} from '@packages/shared/utils/AssetUtils';

import {FileRecordType} from '@packages/systems/core/records/FileRecord';
import {getUploadData} from '@packages/utilities/file-upload';
import {
  paste as pasteActionCreator,
  xscpAssetImportSucceeded,
} from '../../actions/DesignerActionCreators';
import {None, Some, Option, maybe} from '@packages/utilities/fp/option';
import {
  XscpData,
  isXscpData,
  type XscpLibraryImport,
} from '@packages/systems/core/records/Xscp';
import {
  prepareSharedLibraryPaste,
  canCompleteSharedLibraryPaste,
  type SharedLibraryPastePlan,
} from '../xscp/sharedLibraryPaste';
import {Map} from 'immutable';
import {
  ClipboardData,
  ClipboardState,
} from '@packages/systems/designer/clipboard';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {PageState} from '@packages/systems/core/records/PageRecord';
import {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {either, Err, Ok, Result} from '@packages/utilities/fp/result';
import {
  selectCurrentComponentType,
  selectRootElementWithFocusedInstancesExpanded,
  selectSelectedElement,
  selectElementFromSelectedNodeNativeId,
  selectCurrentComponent,
  getLiveComponentMap,
} from '@packages/utilities/platform-selectors';
import {
  cloneElement,
  cloneElementWithIdMapping,
  cloneElementWithoutComponentPropertyBindings,
} from '@packages/systems/element-clone-utils';
import {generateGuid} from '@packages/utilities/guid';
import {remapXscpSectionLinks} from '../xscpUtils';
import {
  furthestTextHolderElement,
  addElement,
  addElementToSlotDefault,
  resolveAnchorOwner,
  resolveSlotDefaultDropFieldId,
} from './DragDropUtils';
import {applyProjectedPlacement} from './SlotDefaults';
import {haveCycles} from '@packages/utilities/platform-validation';
import {resolveSlotDefaultPathContext} from '@packages/utilities/platform';
import {
  getErrorMessage,
  validateChanges,
  validateComponentMapCycles,
  ValidationContext,
} from '@packages/utilities/dom-validation';
import * as DesignerErrorCodes from '@packages/systems/core/constants/ErrorCodes';
import {
  selectIsCanvasFocused,
  selectIsCodeModeWorkflow,
} from '@packages/systems/designer-selectors';
import {selectIsEditingCodeComponent} from '@packages/systems/code-libraries/designer';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {
  removeDynamoBindingsOnPaste,
  removeDynamoBoundPropReferencesOnPaste,
} from './removeDynamoBindingsOnPaste';
import {isInsideDynList} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {lib as layoutLib} from '@packages/systems/layout';
import {getClippedElement} from '../../selectors/ClipboardSelectors';
import {placeGridItem} from '../placeGridItem';
import {Tree} from '@packages/systems/tree';
import {
  prepareEcommerceElementsOnXscp,
  validateEcommerceElementPastePosition,
} from '@packages/shared/render/plugins/Commerce/CommerceElementXscpUtils';
import {importXscpStyles} from '../../LegacyStyleSystem/utils/StyleBlockUtils';
import {
  HistoryItem,
  UndoRedoHistory,
  getMostRecentHistoryItem,
  getUpcomingHistoryItem,
} from '@packages/systems/undoRedo';
import {getPages} from '@packages/shared/utils/PageTreeUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {selectIsInOptimizeMode} from '@packages/systems/optimization/store';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';

export type XscpAssetImportedSuccededAction = DesignerActionFromCreator<
  typeof xscpAssetImportSucceeded
>;

export type SimplePasteAction = DesignerActionFromCreator<
  typeof pasteActionCreator
>;

export type PasteAction = SimplePasteAction | XscpAssetImportedSuccededAction;

const notFound = Expressions.resume([]);

const LINK_LITERAL_PATH: ExpressionPath = [{in: 'Record', at: 'link'}];

type LinkLiteral = {
  name: Qualified<string>;
  value: {
    assetId?: string;
    mode: string;
    domId?: string;
  };
};

const IMAGE_WIDTH_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'width'},
];
const IMAGE_HEIGHT_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'height'},
];

/**
 * Fixes Image elements with numeric width/height values (should be strings).
 * Cross-site paste can introduce this corruption.
 */
export function fixImageDimensions(
  element: ExpressionElement
): ExpressionElement {
  let expr: Expression = EElement(element);
  const elements = Expressions.allElements(expr);

  for (const [id, el] of Object.entries(elements)) {
    if (!isImageElement(el)) continue;

    const data = Expressions.getElementData(el);
    let updatedData = data;

    for (const path of [IMAGE_WIDTH_PATH, IMAGE_HEIGHT_PATH]) {
      const dimExpr = Expressions.getIn(updatedData, path);
      if (!dimExpr || !Expressions.isText(dimExpr)) continue;

      const val = Expressions.getText(dimExpr) as unknown;
      if (typeof val === 'number') {
        console.warn(
          'Cross-site paste: Image dimensions were numeric values instead of strings'
        );
        updatedData = Expressions.setIn(updatedData, path, EText(String(val)));
      }
    }

    if (updatedData !== data) {
      expr = Expressions.updateElement(
        id,
        (currentEl) => Expressions.setElementData(currentEl, updatedData),
        expr
      );
    }
  }

  return Expressions.getElement(expr) ?? element;
}

/*
  This function handles pasting elements with previously deleted user components.
  It checks paste data for missing user components and reintroduces them if necessary.

  @Olena on why this approach was chosen. Two methods were considered:

  (1) Verify that all roots in both component map states (the current and the one from clipboard data) match.
  (2) Identify which user component instances are in the paste element data and confirm that the corresponding user component roots are present in the current component map state.

  The decision was made to proceed with method (2), as it offers a more performant solution with the following reasoning:

  As we continue to enhance user component features, we anticipate a significant increase in the number of user components per project, leading to larger component map states.
  Conversely, the tree structure in the copied element data is inherently less complex than the page element tree, resulting in simpler processing.
*/
export const restoreUserComponentsOnPaste = (
  pastedElement: ExpressionElement,
  clipboardComponentMap: Component.ComponentMap,
  currentComponentMap: Component.ComponentMap
): Component.ComponentMap => {
  // Get all the user component types in the pasted element.
  // We also recursively check user components in user components.
  const getAllComponentTypes = (expr: Expression): Qualified<string>[] => {
    return Expressions.everything(concat)(
      matchExpression({
        Element: (el) => {
          if (isUserComponent(el.type)) {
            const clipboardComponent = clipboardComponentMap.get(el.type);
            if (!clipboardComponent) return Expressions.resume([el.type]);

            const componentRender = Component.getRender(clipboardComponent);
            const nestedComponentTypes = getAllComponentTypes(componentRender);

            return Expressions.resume([el.type].concat(nestedComponentTypes));
          }
          return notFound;
        },
        _: () => notFound,
      })
    )(expr);
  };

  const reducer = (
    updatedComponentMap: Component.ComponentMap,
    componentType: Qualified<string>
  ) => {
    if (updatedComponentMap.has(componentType)) return updatedComponentMap;

    const component = clipboardComponentMap.get(componentType);
    if (!component) {
      reportBug(
        'Unknown Error: Could not find user component root in clipboard',
        'Data for paste component root missing in clipboard',
        {
          pastedElement,
          clipboardComponentMap,
          currentComponentMap,
          ticket: 'https://webflow.atlassian.net/browse/STRUCT-196',
          team: 'structure',
        }
      );

      return updatedComponentMap;
    }

    return updatedComponentMap.set(componentType, component);
  };

  const componentTypes = getAllComponentTypes(EElement(pastedElement));

  return componentTypes.reduce(reducer, currentComponentMap);
};

/**
 * Treats a paste as XSCP when its source site ID differs from the current site.
 * Missing or unparseable XSCP metadata is treated as cross-site.
 * If the source site IDs match then the paste is treated as non-XSCP.
 */
export const isPastingCrossSite = weakMemo((action: PasteAction): boolean => {
  if (action.type !== PASTE || !action.payload) return false;

  const {canCrossSiteCopyPaste} = action.state.UserCapabilitiesStore;
  const isPendingPaste = optionToBool(action.state.DesignerStore.pendingPaste);

  if (
    !canCrossSiteCopyPaste ||
    isPendingPaste ||
    !('clipboardData' in action.payload)
  )
    return false;

  const currentClipboardData: string =
    action.payload.clipboardData['application/json'] ?? '';
  if (!currentClipboardData) return false;

  const destinationSiteId = action.state.SiteDataStore?.siteId;
  if (!destinationSiteId) return true;

  return maybe(true)(
    (data: XscpData) => data.meta?.sourceSiteId !== destinationSiteId
  )(getUncheckedXscpData(action.payload.clipboardData));
});

const isComponentThatDoesNotAcceptChildren = (
  anchorElement: ExpressionElement
): boolean => {
  return (
    isPredefinedComponent(anchorElement) &&
    !isSliderMask(anchorElement) &&
    !isNavbarWrapper(anchorElement)
  );
};

/**
 * Determines paste position relative to an anchor element. Appends inside the anchor by default,
 * except in specific cases where it pastes after the anchor, such as:
 * - The anchor is a user component instance.
 * - The anchor and the pasted element are the same.
 * - The anchor is a typographic element or a component that doesn't accept children.
 * - Special cases like form input wrappers, select nodes, grid rows, and leaf elements.
 */
function getPastePosition({
  componentMap,
  plugins,
  anchorElement,
  anchorPath,
  pastedElement,
}: {
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  anchorElement: ExpressionElement;
  anchorPath: ExpressionPath | null;
  pastedElement: ExpressionElement;
}): 'after' | 'append' {
  const shouldPasteAfter =
    // If pasting into a user component instance, always add after instead of inside
    (isUserComponentInst(componentMap, anchorElement) && !anchorPath) ||
    // If the element to be pasted is the same as the selected selected, paste after.
    anchorElement === pastedElement ||
    // For text holding elements, paste after the target since we can't paste inside.
    isTextHolder(anchorElement) ||
    // For components that don’t accept new children, paste after.
    isComponentThatDoesNotAcceptChildren(anchorElement) ||
    // Don't allow pasting inside of a checkbox or radio button wrapper.
    isFormInputWrapper(anchorElement) ||
    // Don't allow pasting inside of a form <select> element.
    isFormSelect(anchorElement) ||
    // If the target element is a form wrapper, use the same behavior.
    isFormWrapper(anchorElement) ||
    // If the target element is grid row, then just add right after it.
    isGridRow(anchorElement) ||
    // For elements that don't accept children paste after the target
    isLeaf(Plugins.getAtom(plugins, anchorElement), plugins);

  return shouldPasteAfter ? 'after' : 'append';
}

/**
 * Prevents the binding of a single flow to multiple forms,
 * which can occur when pasting elements with forms.
 */
export const removeFlowIdOnPaste = (
  pasteDetails: PasteElementDetail[]
): PasteElementDetail[] => {
  return pasteDetails.map(({element, ...rest}) => {
    const expr = EElement(element);
    const updatedExpr = resetFormElementsInTreeToDefaultSource(expr);
    const updatedElement = Expressions.getElement(updatedExpr);

    return {
      ...rest,
      // we should never have a case where the updated element is undefined
      element: updatedElement ? updatedElement : element,
    };
  });
};

export const setElementStylesOnPaste = (
  pasteDetails: PasteElementDetail[]
): PasteElementDetail[] => {
  return pasteDetails.map((pasteDetail) => ({
    ...pasteDetail,
    element: setPastedGridItemStyleForElement(pasteDetail.element),
  }));
};

const replaceAssets = weakMemo(
  (assetMap: Immutable.Map<string, FileRecordType>) =>
    weakMemo((element: ExpressionElement): ExpressionElement => {
      const updatedExpression = Expressions.everywhere(
        matchExpression({
          Element: (el) => {
            return EElement(replaceAssetsInElement(assetMap)(el));
          },
          _: identity,
        })
      )(EElement(element));
      return Expressions.getElement(updatedExpression) || element;
    })
);

const replaceAssetsInElement = weakMemo(
  (
    assetMap: Immutable.Map<string, FileRecordType>
  ): ((element: ExpressionElement) => ExpressionElement) =>
    pipe([
      replaceImageAsset(assetMap),
      replaceLinkAsset(assetMap),
      replaceLighboxAssets(assetMap),
      replaceBackgroundVideo(assetMap),
    ])
);

// Retained instances keep asset and link values inside property overrides,
// so remapping must reach those expressions as well as ordinary element fields.
const rewriteSharedLibraryPasteReferences = (
  assetMap: Map<string, FileRecordType>,
  element: ExpressionElement
): ExpressionElement => {
  const ids: Record<string, string> = {};
  assetMap.forEach((asset, sourceId) => {
    ids[sourceId] =
      asset.type === 'LOTTIE' || asset.type === 'RIVE'
        ? asset.cdnUrl
        : asset._id;
  });
  const expression = Expressions.everywhere(
    matchExpression({
      Literal: (literal) => {
        const value = literal.value;
        if (
          getPluginName(literal.name) !== 'Basic' ||
          getName(literal.name) !== 'Link' ||
          !value ||
          typeof value !== 'object' ||
          Array.isArray(value)
        ) {
          return ELiteral(literal);
        }
        if (typeof value.assetId !== 'string') return ELiteral(literal);
        const asset = assetMap.get(value.assetId);
        return asset
          ? ELiteral({...literal, value: {...value, assetId: asset._id}})
          : ELiteral(literal);
      },
      _: identity,
    })
  )(updateAssetIdsInExpression(EElement(element), ids));
  return Expressions.getElement(expression) ?? element;
};
const ATTR_SRC_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'src'},
];
const IMG_ID_PATH: ExpressionPath = [{in: 'Record', at: 'img'}];

type ImageLiteral = {
  name: Qualified<string>;
  value: {
    id: string;
  };
};

const replaceImageAsset =
  (assetMap: Immutable.Map<string, FileRecordType>) =>
  (element: ExpressionElement) => {
    const imageLiteral = Expressions.getElementDataAt(
      element,
      IMG_ID_PATH,
      'Literal',
      undefined
    ) as ImageLiteral | undefined;

    if (!imageLiteral || !imageLiteral.value.id) {
      return element;
    }

    const asset = assetMap.get(imageLiteral.value.id);

    if (!asset) {
      return element;
    }

    const imageWithUpdatedId = Expressions.setIn(
      element.data,
      IMG_ID_PATH,
      ELiteral({
        name: qualify(Plugins.name('Asset'), typeName('Image')),
        value: {id: asset.get('_id')},
      })
    );
    const updatedData = Expressions.setIn(
      imageWithUpdatedId,
      ATTR_SRC_PATH,
      EText(asset.get('cdnUrl'))
    );
    return Expressions.setElementData(element, updatedData);
  };

const replaceLinkAsset =
  (assetMap: Immutable.Map<string, FileRecordType>) =>
  (element: ExpressionElement) => {
    const linkLiteral = getLinkLiteral(element);

    if (!linkLiteral || !linkLiteral.value.assetId) {
      return element;
    }

    const asset = assetMap.get(linkLiteral.value.assetId);

    if (!asset) {
      return element;
    }
    const updatedData = Expressions.setIn(
      element.data,
      LINK_LITERAL_PATH,
      ELiteral({
        name: qualify(Plugins.name('Asset'), typeName('Link')),
        value: {
          mode: linkLiteral.value.mode,
          assetId: asset.get('_id'),
        },
      })
    );
    return Expressions.setElementData(element, updatedData);
  };

const JSON_ITEMS_PATH: ExpressionPath = [
  {in: 'Record', at: 'json'},
  {in: 'Record', at: 'items'},
];

const JSON_ITEM_IMAGE_TYPE_PATH: ExpressionPath = [{in: 'Record', at: 'type'}];

const JSON_ITEM_UNION_IMAGE_ID_PATH: ExpressionPath = [
  {in: 'Union', tag: 'Image'},
  {in: 'Record', at: '_id'},
];

const JSON_ITEM_IMAGE_ID_PATH: ExpressionPath = [{in: 'Record', at: '_id'}];

const JSON_ITEM_UNION_IMAGE_URL_PATH: ExpressionPath = [
  {in: 'Union', tag: 'Image'},
  {in: 'Record', at: 'url'},
];

const JSON_ITEM_UNION_IMAGE_FILE_NAME_PATH: ExpressionPath = [
  {in: 'Union', tag: 'Image'},
  {in: 'Record', at: 'fileName'},
];

const updateJsonItem = (item: Expression) => (asset: FileRecordType) => {
  const updatedId = Expressions.sparseSetIn(
    item,
    JSON_ITEM_UNION_IMAGE_ID_PATH,
    EText(asset.get('_id'))
  );
  const updatedIdAndUrl = Expressions.sparseSetIn(
    updatedId,
    JSON_ITEM_UNION_IMAGE_URL_PATH,
    EText(asset.get('cdnUrl'))
  );
  return Expressions.sparseSetIn(
    updatedIdAndUrl,
    JSON_ITEM_UNION_IMAGE_FILE_NAME_PATH,
    EText(asset.get('fileName'))
  );
};

const getJsonItemImageAsset =
  (item: Expression) => (assetMap: Immutable.Map<string, FileRecordType>) => {
    const typeExpr = Expressions.getIn(item, JSON_ITEM_IMAGE_TYPE_PATH);
    if (!typeExpr) return undefined;
    const type = Expressions.getText(typeExpr);
    if (type !== 'image') return undefined;
    const idExpr = Expressions.getIn(item, JSON_ITEM_IMAGE_ID_PATH);
    if (!idExpr) return undefined;
    const id = Expressions.getText(idExpr);
    if (!id) return undefined;
    return assetMap.get(id);
  };

const replaceLighboxAssets =
  (assetMap: Immutable.Map<string, FileRecordType>) =>
  (element: ExpressionElement) => {
    if (!isLightboxWrapper(element) || !hasJsonItems(element)) {
      return element;
    }
    const updatedData = Expressions.updateIn(
      element.data,
      JSON_ITEMS_PATH,
      (list) => {
        const items = Expressions.getList(list);
        if (!items) return list;
        const updatedItems = items.map((item) => {
          const itemValue = Expressions.getUnion(item)?.value;
          if (!itemValue) return item;
          const assets = getJsonItemImageAsset(itemValue)(assetMap);
          if (!assets) return item;
          return updateJsonItem(item)(assets);
        });
        return EList(updatedItems);
      }
    );
    return Expressions.setElementData(element, updatedData);
  };

const hasJsonItems = (element: ExpressionElement) => {
  const jsonItems = Expressions.getIn(
    Expressions.getElementData(element),
    JSON_ITEMS_PATH
  );
  if (!jsonItems) return false;
  const itemsExpr = Expressions.getList(jsonItems);
  return itemsExpr !== null && itemsExpr.length > 0;
};

const BGVIDEO_URLS_PATH: ExpressionPath = [
  {in: 'Record', at: 'bgvideo'},
  {in: 'Record', at: 'video_urls'},
];

const BGVIDEO_TYPE_PATH: ExpressionPath = [
  {in: 'Record', at: 'bgvideo'},
  {in: 'Record', at: 'type'},
];

const ATTR_VIDEO_URLS_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'data-video-urls'},
];

const hasBackgroundVideoUrls = (element: ExpressionElement) => {
  const urlPathsExpr = Expressions.getIn(element.data, BGVIDEO_URLS_PATH);
  if (!urlPathsExpr) return false;
  const urlPaths = Expressions.getList(urlPathsExpr);
  return urlPaths && urlPaths.length > 0;
};

const isBackgroundVideoEl = (element: ExpressionElement) =>
  Expressions.getElementDataAt(
    element,
    BGVIDEO_TYPE_PATH,
    'Text',
    undefined
  ) !== undefined;

// When a new asset is added `variant` is an Immutable.Map, as the data comes from the XSCP_ASSET_IMPORT_SUCCEEDED action.
// Before it gets re-ified into a FileRecord, an Immutable.fromJS is called on it.
// This turns all nested objects and arrays into Maps and Lists.
// However, if the asset already exists in the project, it is coming from the AssetStore, which has re-ified the response into a FileRecord.
// Before turning it into a FileRecord, a toJS is called on the Immutable.Map from the action's payload, and the reification of FileRecord does not turn nested objects and arrays into the respective Immutable types, which therefore makes `variant`, a POJO.
const getCdnUrls = map(
  (variant: FileRecordType | Immutable.Map<string, string>) =>
    isMap(variant) ? variant.get('cdnUrl') : variant.cdnUrl
);

function isMap(
  variant: FileRecordType | Immutable.Map<string, string>
): variant is Immutable.Map<string, string> {
  return Map.isMap(variant);
}

const getBGVideoVariantUrls =
  (assetMap: Immutable.Map<string, FileRecordType>) =>
  (urlExpr: Expression) => {
    const urlList = Expressions.getList(urlExpr);
    if (!urlList || !urlList[0]) return urlExpr;
    const firstUrl = Expressions.getText(urlList[0]);
    if (!firstUrl) return urlExpr;
    const uploadData = getUploadData(firstUrl);
    const guid = uploadData.guid;
    const asset = assetMap.get(guid);
    if (!asset) return urlExpr;
    // @ts-expect-error - expect to be a FileRecord
    const cdnUrls = getCdnUrls(asset.get('variants'));
    const cdnUrlsEText = cdnUrls.map((url: string) => EText(url));
    const updatedUrls = EList(cdnUrlsEText);
    return updatedUrls;
  };

const getUpdatedBGVideoUrls = (updatedPaths: Expression | undefined) => {
  if (!updatedPaths) return undefined;
  const updatedUrls = Expressions.getIn(updatedPaths, BGVIDEO_URLS_PATH);
  if (!updatedUrls) return undefined;
  const updatedUrlList = Expressions.getList(updatedUrls);
  if (!updatedUrlList) return undefined;
  return updatedUrlList.map((url) => Expressions.getText(url)).join(', ');
};

const updateBackgroundVideoUrls =
  (assetMap: Immutable.Map<string, FileRecordType>) =>
  (element: ExpressionElement) => {
    const updatedPaths = Expressions.updateIn(
      element.data,
      BGVIDEO_URLS_PATH,
      getBGVideoVariantUrls(assetMap)
    );
    const updatedUrls = getUpdatedBGVideoUrls(updatedPaths);
    if (!updatedUrls) return element;
    const updatedData = Expressions.sparseSetIn(
      updatedPaths,
      ATTR_VIDEO_URLS_PATH,
      EText(updatedUrls)
    );

    return Expressions.setElementData(element, updatedData);
  };

const replaceBackgroundVideo =
  (assetMap: Immutable.Map<string, FileRecordType>) =>
  (element: ExpressionElement) =>
    isBackgroundVideoEl(element) && hasBackgroundVideoUrls(element)
      ? updateBackgroundVideoUrls(assetMap)(element)
      : element;

const keepXscpData = (data: unknown): Option<XscpData> =>
  isXscpData(data) ? Some(data as XscpData) : None;

export const CROSS_SITE_COPY_WITH_USER_COMPONENT_ERROR =
  'To paste content with components here, return to the original site and copy it using “Copy to another site.”';

const containsSerializedUserComponentInstance = (nodes: unknown): boolean => {
  if (!Array.isArray(nodes)) return false;

  for (const node of nodes) {
    if (
      node !== null &&
      typeof node === 'object' &&
      (node as Record<string, unknown>).type === 'Symbol'
    ) {
      return true;
    }
  }

  return false;
};

const xscpDataContainsUserComponentInstance = (data: XscpData): boolean =>
  (data.payload.expandUserComponents === false ||
    data.payload.libraryComponents !== undefined) &&
  containsSerializedUserComponentInstance(data.payload.nodes);

const getUncheckedXscpData = weakMemo(
  (savedClipboardData: {[key: string]: string}): Option<XscpData> => {
    const clipboard: string = savedClipboardData['application/json'] ?? '';
    const json = parse(clipboard);
    return json === undefined ? None : keepXscpData(json);
  }
);

export const getXscpData = (
  savedClipboardData: {
    [key: string]: string;
  },
  action?: PasteAction
): Option<XscpData> => {
  const plan = action ? getSharedLibraryPastePlan(action) : None;
  const hasPlan = optionToBool(plan);
  const data = hasPlan
    ? plan.map((value) => value.xscpData)
    : getUncheckedXscpData(savedClipboardData);
  return data.chain((value) =>
    xscpDataContainsUserComponentInstance(value) && !hasPlan
      ? None
      : Some(value)
  );
};

// Share one prepared plan per action so all reducers use the same generated IDs.
const getSharedLibraryPastePlan = weakMemo(
  (action: PasteAction): Option<SharedLibraryPastePlan> => {
    if (
      action.type !== PASTE ||
      !action.payload ||
      !('clipboardData' in action.payload) ||
      !isPastingCrossSite(action) ||
      !getFeatureFlagOutsideOfReact(FEATURE_FLAGS.FAST_COPY)
    )
      return None;
    return getUncheckedXscpData(action.payload.clipboardData).chain((data) => {
      try {
        const plan = prepareSharedLibraryPaste(action.state, data);
        return plan ? Some(plan) : None;
      } catch {
        const references =
          data.payload?.libraryRetention?.data?.payload?.libraryComponents;
        const libraries = Array.isArray(references)
          ? references.filter(
              (reference) =>
                Array.isArray(reference?.origin) &&
                typeof reference.origin[0] === 'string' &&
                typeof reference.profileId === 'string'
            )
          : [];
        // eslint-disable-next-line webflow/better-bug-reporting -- ticket lives in Linear, not Jira.
        reportBug(
          'Shared library paste preparation failed',
          'Falling back to expanded elements',
          {
            sourceSiteId: data.meta?.sourceSiteId,
            destinationSiteId: action.state.SiteDataStore?.siteId,
            sourceLibraryVersionIds: [
              ...new Set(libraries.map((reference) => reference.origin[0])),
            ],
            destinationLibraryVersionIds: Object.values(
              action.state.DesignerStore.libraries
            )
              .filter((library) =>
                libraries.some(
                  (reference) => reference.profileId === library.profileId
                )
              )
              .map((library) => library.pluginId),
            team: 'structure',
            ticket: 'https://linear.app/webflow/issue/DES-7785',
          }
        );
        return None;
      }
    });
  }
);

export const getSharedLibraryPasteImport = (
  action: PasteAction
): Option<XscpLibraryImport> => {
  if (!action.state) return None;
  if (action.type === XSCP_ASSET_IMPORT_SUCCEEDED) {
    return action.state.DesignerStore.pendingPaste.chain((pending) =>
      pending.libraryImport ? Some(pending.libraryImport) : None
    );
  }
  return getSharedLibraryPastePlan(action).map((plan) => plan.libraryImport);
};

// Expose resource changes only when this action can insert elements.
// Rejected pastes and pastes still waiting for assets commit nothing.
export const getCommittedSharedLibraryPasteImport = (
  action: PasteAction
): Option<XscpLibraryImport> =>
  getSharedLibraryPasteImport(action).chain((libraryImport) =>
    okToOption(getPasteDetailsBeforeCloning(action)).chain((details) =>
      details.length > 0 ? Some(libraryImport) : None
    )
  );

export const isUnsupportedCrossSiteComponentPaste = (
  action: PasteAction
): boolean => {
  if (
    action.type !== PASTE ||
    !action.payload ||
    !action.state ||
    !('clipboardData' in action.payload)
  ) {
    return false;
  }

  if (!isPastingCrossSite(action)) return false;

  if (optionToBool(getSharedLibraryPastePlan(action))) return false;

  return maybe(false)(xscpDataContainsUserComponentInstance)(
    getUncheckedXscpData(action.payload.clipboardData)
  );
};

/** Reads native XSCP for a same-site paste when the local clipboard state is unavailable or stale. */
const shouldUseNativeXscpDataForSameSitePaste = weakMemo(
  (action: PasteAction): boolean => {
    if (
      action.type !== PASTE ||
      !action.payload ||
      !('clipboardData' in action.payload)
    )
      return false;

    if (isPastingCrossSite(action)) return false;

    const {canCrossSiteCopyPaste} = action.state.UserCapabilitiesStore;
    const isPendingPaste = optionToBool(
      action.state.DesignerStore.pendingPaste
    );
    if (!canCrossSiteCopyPaste || isPendingPaste) return false;

    const currentClipboardData =
      action.payload.clipboardData['application/json'] ?? '';
    const lastSavedClipboardData =
      action.state.DesignerStore.lastNativeClipboardDataSaved[
        'application/json'
      ] ?? '';
    const hasClipboardState =
      action.state.ClipboardStore && optionToBool(action.state.ClipboardStore);

    return (
      (!hasClipboardState || currentClipboardData !== lastSavedClipboardData) &&
      optionToBool(getUncheckedXscpData(action.payload.clipboardData))
    );
  }
);

/** Routes paste handling through serialized XSCP data, including same-site native clipboard fallbacks. */
export const isPastingSerializedXscp = weakMemo(
  (action: PasteAction): boolean =>
    isPastingCrossSite(action) ||
    shouldUseNativeXscpDataForSameSitePaste(action)
);

export const getXscpAssets = (action: PasteAction): FileRecordProps[] => {
  if (action.type === XSCP_ASSET_IMPORT_SUCCEEDED) {
    return extractArray(
      action.state.DesignerStore.pendingPaste.map(
        (pending) => pending.xscpData.payload.assets
      )
    );
  }
  if (!isPastingCrossSite(action)) return emptyArray;
  if (!('clipboardData' in action.payload)) return emptyArray;
  const xscpData = getXscpData(action.payload?.clipboardData, action);
  const assets = xscpData.map((data) => data.payload.assets);
  return extractArray(assets);
};

export const getExistingXscpAssets = (
  action: PasteAction
): Map<string, FileRecord> =>
  getXscpAssets(action).reduce((assetMap, asset) => {
    const foundAsset = findByFileHash(asset)(action.state.AssetStore.assets);

    return foundAsset ? assetMap.set(asset._id, foundAsset) : assetMap;
  }, Map<string, FileRecord>());

export const getNewXscpAssets = (action: PasteAction) => {
  return getXscpAssets(action).filter((asset) => {
    return !findByFileHash(asset)(action.state.AssetStore.assets);
  });
};

export const getNewXscpAssetsMap = (action: PasteAction) => {
  return getNewXscpAssets(action).reduce((assetMap, asset) => {
    return assetMap.set(asset._id, asset as FileRecord);
  }, Map<string, FileRecord>());
};

export const isPastingNewCrossSiteAssets = compose(
  (assets: FileRecordProps[]) => assets.length > 0
)(getNewXscpAssets);

function updateXscpSectionLinkAfterCloning(
  newElement: ExpressionElement,
  idMap: Readonly<Record<string, string>>
): ExpressionElement {
  return (
    Expressions.getElement(
      remapXscpSectionLinks(EElement(newElement), idMap)
    ) ?? newElement
  );
}

const getLinkLiteral = (element: ExpressionElement) =>
  Expressions.getElementDataAt(
    element,
    LINK_LITERAL_PATH,
    'Literal',
    undefined
  ) as LinkLiteral | undefined;

/**
 * Determines where to paste content in a UI structure.
 * Finds the deepest text node within the rootExpression related to the selected UI element.
 * Returns this node or the selected element itself if no text node is found.
 */
const getPasteAnchor = (
  designerState: DesignerStateType,
  uiNodeState: UiNodeStateType
): null | {
  anchorElement: ExpressionElement;
  anchorPath: ExpressionPath | null;
  validationAnchorPath: ExpressionPath | null;
} => {
  const state = {DesignerStore: designerState, UiNodeStore: uiNodeState};
  // use selectElementFromSelectedNodeNativeId to address cases
  // where the selected element is a user component instance property
  const selectedElement = selectElementFromSelectedNodeNativeId(state);
  const currentComponent = selectCurrentComponent(state);
  const {components} = designerState;
  const root = Component.getRender(currentComponent);

  if (!selectedElement) return null;

  const furthestTextNode = furthestTextHolderElement(root)(selectedElement);

  const anchorElement = furthestTextNode ? furthestTextNode : selectedElement;

  return {
    anchorElement,
    // Placement keeps the narrow contract: a non-null anchorPath names a field
    // of anchorElement's own data.
    anchorPath: getTargetedSlotAnchorPath(
      uiNodeState.selectedNativeIdPath,
      uiNodeState.focusedSymbolNativeIdPath,
      anchorElement,
      components
    ),
    // Validation additionally recovers the slot when the anchor is content
    // already sitting in one. See resolveValidationSlotAnchorPath.
    validationAnchorPath: resolveValidationSlotAnchorPath(
      uiNodeState.selectedNativeIdPath,
      uiNodeState.focusedSymbolNativeIdPath,
      anchorElement,
      currentComponent,
      components
    ),
  };
};

/**
 * Handles the pasting of a tab pair (link and pane).
 * Ensures when pasting a tab pair, the tab names are unique
 * and the corresponding pair is inserted& linked correctly.
 */
const handleTabPairPasting = (
  pasteDetail: PasteElementDetail,
  clipboardData: ClipboardData,
  root: Expression,
  unbindForPaste: (element: ExpressionElement) => ExpressionElement
): Result<string, PasteElementDetail[]> => {
  const {element, anchorId} = pasteDetail;
  // Get corresponding tab pair from clipboard root.
  // For "cut" operations, pair is missing from current component map.
  const clipboardRoot = selectRootElementWithFocusedInstancesExpanded({
    DesignerStore: clipboardData.designerState,
    UiNodeStore: clipboardData.uiNodeState,
  });
  if (!clipboardRoot) return Err('Clipboard root not found.');

  const wrapper = Expressions.closestElement(
    root,
    anchorId,
    (el: ExpressionElement) => isTabsWrapper(el)
  );
  if (!wrapper) return Err('TabsWrapper not found.');

  // get the pair from the clipboard data to preserve the contents of the pair
  const pair = maybe<[ExpressionElement, ExpressionElement] | null>(null)(
    identity<[ExpressionElement, ExpressionElement]>
  )(tabsPair(clipboardData.clippedElement)(clipboardRoot));

  if (!pair) return Err('Missing Tabs pair element');
  // the pair is rebuilt from the clipboard, so it needs the same unbinding
  const rawLink = unbindForPaste(pair[0]);
  const rawPane = unbindForPaste(pair[1]);

  // generate a new tab name based on the existing tab names
  const tabName = getTabAttr(element);
  const tabPrefix = tabName.replace(/\s*\d*\s*$/, '') || 'Tab';
  const newName = generateUniqueTabNameFromWrapper(wrapper, tabPrefix);
  if (!newName) return Err('Unable to generate new tab name.');

  // use the new tab name generate a linked tab pair
  // we also refresh element IDs to account for multiple pastes
  const link = replaceTabPairData(rawLink, newName);
  const pane = replaceTabPairData(rawPane, newName);

  const menu = findChild(wrapper, isTabsMenu);
  const content = findChild(wrapper, isTabsContent);
  if (!menu || !content) return Err('Menu or content not found.');

  return Ok([
    {
      element: link,
      anchorId: menu.id,
      anchorPath: null,
      position: 'append',
    },
    {
      element: pane,
      anchorId: content.id,
      anchorPath: null,
      position: 'append',
    },
  ]);
};

/**
 * Retrieves necessary details for a same-site paste operation before cloning.
 * These details are used to validate the paste operation
 * and determine the placement of the pasted element.
 */
// prettier-ignore
const _getSameSitePasteDetailsBeforeCloning =
  weakMemo((clipboardState: ClipboardState) =>
  weakMemo((designerState: DesignerStateType) =>
  weakMemo((collectionState: CollectionStateType) =>
  weakMemo((pageState: PageState) =>
  weakMemo((uiNodeState: UiNodeStateType) =>
  singleMemo((isInPageBuildMode: boolean) =>
  singleMemo((isInCampaignBuildMode: boolean) =>
  singleMemo((isCampaignSite: boolean) =>
  weakMemo((cmsRtfComponentIds: readonly string[]): Result<string, PasteElementDetail[]> => {
  return noneToErr('The clipboard is empty.')(clipboardState)
    .chain((clipboard: ClipboardData) => {
      const anchorDetails = getPasteAnchor(designerState, uiNodeState);
      if (!anchorDetails) return Err('An element needs to be selected to paste into.');

      const {
        anchorElement: pasteAnchor,
        anchorPath,
        validationAnchorPath,
      } = anchorDetails;

      // we need to restore the user components to validate
      // the paste operation on the full component map
      const componentMap = restoreUserComponentsOnPaste(
        clipboard.clippedElement,
        clipboard.designerState.components,
        designerState.components
      );
      // check for cyclic components here to prevent infinite loop
      if (haveCycles(componentMap)) return Err('Cyclic components detected.');

      const {plugins} = designerState;

      const state = {DesignerStore: designerState, UiNodeStore: uiNodeState}
      const currentComponent = selectCurrentComponent(state)
      const currentComponentType = selectCurrentComponentType(state)
      const sourceCurrentComponentType = selectCurrentComponentType({
        DesignerStore: clipboard.designerState,
        UiNodeStore: clipboard.uiNodeState,
      })
      const root = Component.getRender(currentComponent);

      // Each personalized campaign binds to its own hidden collection, so a
      // pasted or duplicated copy must not carry the source's CMS bindings.
      const sourceRoot = Component.getRender(
        selectCurrentComponent({
          DesignerStore: clipboard.designerState,
          UiNodeStore: clipboard.uiNodeState,
        })
      );
      // Bindings inside a collection list resolve against the list's items,
      // not the page, so the CMS validator decides whether they still fit.
      const keepBindings =
        !isCampaignSite ||
        isInsideDynList(sourceRoot)(clipboard.clippedElement);
      // The selection path ends at the selected node, so every focused instance
      // is on it; the focused path alone would drop its last instance.
      const focusedDepth = uiNodeState.focusedSymbolNativeIdPath.length;
      const focusedInstances =
        isCampaignSite && focusedDepth > 0 && uiNodeState.selectedNativeIdPath
          ? getAllComponentInstances(uiNodeState.selectedNativeIdPath, designerState.components)
          : [];
      const pageBody = getBody(designerState.components);
      const outermostInstance = focusedInstances[0];
      const focusedInstance =
        outermostInstance &&
        !(pageBody && isInsideDynList(EElement(pageBody))(outermostInstance))
          ? focusedInstances[focusedDepth - 1]
          : undefined;
      const unbindForPaste = (el: ExpressionElement): ExpressionElement => {
        const unbound = keepBindings
          ? el
          : removeDynamoBindingsOnPaste(el, componentMap, plugins);
        return focusedInstance
          ? removeDynamoBoundPropReferencesOnPaste(unbound, focusedInstance, currentComponent, plugins)
          : unbound;
      };
      const rawChild = unbindForPaste(clipboard.clippedElement);

      const position = getPastePosition({
        componentMap,
        plugins,
        anchorElement: pasteAnchor,
        anchorPath,
        pastedElement: rawChild,
      });

      let rawParent = pasteAnchor;
      if (position === 'after') {
        const maybeParent = Expressions.getParent(root, pasteAnchor.id);
        rawParent = maybeParent ?? rawParent;
      }

      const body = getBody(componentMap);
      if (!body) return Err('No body found.');

      // clone the child, stage the components, and validate the staged paste operation
      const clonedChild = cloneElement(rawChild, componentMap, generateGuid());

      const stagedComponents = updateComponentRenderInComponentMap(
        componentMap,
        currentComponentType,
        addElementAtAnchorInPath(root, clonedChild, pasteAnchor, position, anchorPath, componentMap)
      )

      const slotDefaultOwner = resolveAnchorOwner({
        componentMap,
        designerState,
        uiNodeState,
        anchorId: pasteAnchor.id,
      });
      const slotDefaultFieldId = resolveSlotDefaultDropFieldId({
        component: slotDefaultOwner.component,
        componentType: slotDefaultOwner.componentType,
        anchorId: pasteAnchor.id,
        position,
        anchorPath: anchorPath ?? null,
        componentMap,
        plugins,
      });
      if (slotDefaultFieldId) {
        const slotDefaultCycleError = getErrorMessage(
          validateComponentMapCycles(
            addElementToSlotDefault(
              componentMap,
              slotDefaultOwner.componentType,
              slotDefaultFieldId,
              clonedChild,
              position
            ),
            DesignerErrorCodes.getPasteMessage
          )
        );
        if (slotDefaultCycleError) return Err(slotDefaultCycleError);
      }

      const rawParentAdjustmentResult = adjustedRawParentForSlotDefaultPaste(
        uiNodeState,
        root,
        anchorPath ?? null,
        componentMap,
        plugins
      );
      if (rawParentAdjustmentResult) {
        if (rawParentAdjustmentResult.kind === 'error') {
          return Err(rawParentAdjustmentResult.error);
        }
        rawParent = rawParentAdjustmentResult.newRawParent;
      }

      const validationContext: ValidationContext = {
        collections: collectionState.collections,
        componentInstanceCounts: designerState.componentInstanceCounts,
        currentComponentType,
        currentPageId: designerState.currentPageId,
        plugins: designerState.plugins,
        userComponentUsageMap: designerState.userComponentUsageMap,
        cmsRtfComponentIds,
        pages: getPages(pageState),
        rawParent,
        rawChild,
        anchorPath: validationAnchorPath,
        isNewElement: true,
        sourceData: {
          componentMap: getLiveComponentMap(clipboard.designerState),
          currentComponentType: sourceCurrentComponentType,
          currentPageId: clipboard.designerState.currentPageId,
          collections: clipboard.collectionState.collections,
          pages: getPages(clipboard.pageState),
        },
        runtimeArgs: {
          runtimeEnvironment: WebflowEnvironments.DESIGNER,
          isInPageBuildMode,
          isInCampaignBuildMode,
          staleComponents: designerState.staleComponents,
        },
        featureFlags: {
          isComponentSlotDefaultsEnabled: getFeatureFlagOutsideOfReact(
            FEATURE_FLAGS.COMPONENT_SLOT_DEFAULTS
          ),
        },
        libraryPluginIds: selectLibraryPluginIds(designerState.libraries),
      };

      return validateChanges(
        componentMap,
        stagedComponents,
        validationContext,
        DesignerErrorCodes.getPasteMessage
      ).chain((element: ExpressionElement) => {
        // after validating the operation, we can proceed with the paste
        // pass the raw child which will later be cloned and added to the component map
        const operation: PasteElementDetail = {
          element: rawChild,
          anchorId: pasteAnchor.id,
          anchorPath,
          position,
        };
        return isTabsPair(element)
          ? handleTabPairPasting(operation, clipboard, root, unbindForPaste)
          : Ok([operation]);
      });
    });
  }
)))))))));

// prettier-ignore
export const getSameSitePasteDetailsBeforeCloning = ({
  state: {
    ClipboardStore: clipboardState,
    DesignerStore: designerState,
    CollectionStore: collectionState,
    PageStore: pageState,
    UiNodeStore: uiNodeState,
    isInPageBuildMode: isInPageBuildMode,
    isInCampaignBuildMode: isInCampaignBuildMode,
    isCampaignSite,
    cmsRtfComponentIds,
  },
}: {
  state: {
    ClipboardStore: ClipboardState;
    DesignerStore: DesignerStateType;
    CollectionStore: CollectionStateType;
    PageStore: PageState;
    UiNodeStore: UiNodeStateType;
    isInPageBuildMode: boolean;
    isInCampaignBuildMode: boolean;
    isCampaignSite: boolean;
    cmsRtfComponentIds: readonly string[];
  }
}): Result<string, PasteElementDetail[]> => /* eslint-disable no-unexpected-multiline */
  _getSameSitePasteDetailsBeforeCloning
    (clipboardState)
    (designerState)
    (collectionState)
    (pageState)
    (uiNodeState)
    (isInPageBuildMode)
    (isInCampaignBuildMode)
    (isCampaignSite)
    (cmsRtfComponentIds)
/* eslint-enable no-unexpected-multiline */

export const getEventualCrossSitePasteDetails = (
  action: PasteAction,
  preparedLibraryPlan?: SharedLibraryPastePlan
): Result<string, PasteElementDetail[]> => {
  const libraryPlan =
    preparedLibraryPlan ??
    maybe<SharedLibraryPastePlan | null>(null)(
      identity<SharedLibraryPastePlan>
    )(getSharedLibraryPastePlan(action));
  return noneToErr("Couldn't read native clipboard data.")(
    libraryPlan
      ? Some(libraryPlan.xscpData)
      : getUncheckedXscpData(
          'clipboardData' in action.payload ? action.payload.clipboardData : {}
        )
  ).chain((xscpData: XscpData) => {
    if (
      isPastingCrossSite(action) &&
      xscpDataContainsUserComponentInstance(xscpData) &&
      !libraryPlan
    ) {
      return Err(CROSS_SITE_COPY_WITH_USER_COMPONENT_ERROR);
    }

    const {
      CollectionStore: collectionState,
      PageStore: pageState,
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
      CommerceStore: commerceState,
    } = action.state;

    const isInPageBuildMode = selectDesignerAbility('isInPageBuildMode')(
      action.state
    );

    const anchor = getPasteAnchor(designerState, uiNodeState);
    if (!anchor) return Err('An element needs to be selected to paste into.');

    const {plugins} = designerState;
    const componentMap = libraryPlan
      ? designerState.components.union(libraryPlan.libraryImport.components)
      : designerState.components;
    const tree = Tree.create(componentMap);
    const child =
      libraryPlan?.element ??
      tree.getElementWithJSONNodes(xscpData.payload.nodes, plugins);
    if (!child) {
      return Err('Could not find an element to paste across projects.');
    }

    const fixedChild = fixImageDimensions(child);

    const currentComponent = selectCurrentComponent(action.state);
    const root = Component.getRender(currentComponent);

    const position = getPastePosition({
      componentMap,
      plugins,
      anchorElement: anchor.anchorElement,
      anchorPath: anchor.anchorPath,
      pastedElement: fixedChild,
    });

    let rawParent = anchor.anchorElement;
    if (position === 'after') {
      const maybeParent = Expressions.getParent(root, anchor.anchorElement.id);
      rawParent = maybeParent ?? rawParent;
    }

    // ecommerce XSCP validation
    const ecommerceValidationResult = validateEcommerceElementPastePosition(
      rawParent,
      root,
      fixedChild,
      getPages(pageState).get(designerState.currentPageId),
      commerceState?.status?.cmsState === 'ready'
    );
    if (getErrorMessage(ecommerceValidationResult)) {
      return ecommerceValidationResult;
    }

    const clonedChild = cloneElement(fixedChild, componentMap, generateGuid());

    const stagedComponents = updateComponentRenderInComponentMap(
      componentMap,
      selectCurrentComponentType(action.state),
      addElementAtAnchorInPath(
        root,
        clonedChild,
        anchor.anchorElement,
        position,
        anchor.anchorPath,
        componentMap
      )
    );

    const validationContext: ValidationContext = {
      collections: collectionState.collections,
      componentInstanceCounts: designerState.componentInstanceCounts,
      currentComponentType: selectCurrentComponentType({
        DesignerStore: designerState,
        UiNodeStore: uiNodeState,
      }),
      currentPageId: designerState.currentPageId,
      plugins: designerState.plugins,
      userComponentUsageMap: designerState.userComponentUsageMap,
      cmsRtfComponentIds: selectAllCmsRtfComponentIds(action.state),
      pages: getPages(pageState),
      rawParent,
      rawChild: fixedChild,
      anchorPath: anchor.validationAnchorPath,
      isNewElement: true,
      runtimeArgs: {
        runtimeEnvironment: WebflowEnvironments.DESIGNER,
        isInPageBuildMode,
        isInCampaignBuildMode: selectDesignerAbility('isInCampaignBuildMode')(
          action.state
        ),
        staleComponents: designerState.staleComponents,
      },
      featureFlags: {},
      libraryPluginIds: selectLibraryPluginIds(designerState.libraries),
    };

    return validateChanges(
      componentMap,
      stagedComponents,
      validationContext,
      DesignerErrorCodes.getPasteMessage
    ).chain((element: ExpressionElement) => {
      if (isTabsPair(element)) {
        // Validation returns an expanded tree. Build the tab pair from fixedChild
        // to preserve retained library instances.
        const tabElement = libraryPlan ? fixedChild : element;
        const wrapper = Expressions.closestElement(
          root,
          anchor.anchorElement.id,
          isTabsWrapper
        );
        if (!wrapper) return Err('Missing Tabs wrapper element.');

        const menu = findChild(wrapper, isTabsMenu);
        const content = findChild(wrapper, isTabsContent);
        if (!menu || !content) return Err('Menu or content not found.');

        const tabName = getTabAttr(tabElement);
        const tabPrefix = tabName.replace(/\s*\d*\s*$/, '') || 'Tab';
        const newName = generateUniqueTabNameFromWrapper(wrapper, tabPrefix);
        if (!newName) return Err('Unable to generate new tab name.');

        const intialId =
          ('initialId' in action.payload && action.payload.initialId) ||
          generateGuid();

        return Ok([
          {
            element: isTabsLinkElement(tabElement)
              ? replaceTabPairData(tabElement, newName)
              : generateNewTabsLinkExpression(newName, intialId),
            anchorId: menu.id,
            position: 'append',
          },
          {
            element: isTabsPane(tabElement)
              ? replaceTabPairData(tabElement, newName)
              : generateNewTabsPaneExpression(newName, intialId),
            anchorId: content.id,
            position: 'append',
          },
        ]);
      }

      // If Ecommerce element, will need some binding preparation before pasting
      const preparedElement = prepareEcommerceElementsOnXscp(
        fixedChild,
        collectionState
      );

      return Ok([
        {
          element: preparedElement,
          anchorId: anchor.anchorElement.id,
          position,
        },
      ]);
    });
  });
};

const getCrossSitePasteDetailsBeforeCloning = (action: SimplePasteAction) => {
  return getEventualCrossSitePasteDetails(action).map(
    (details: PasteElementDetail[]) => {
      if (isPastingNewCrossSiteAssets(action)) return emptyArray;

      const assetMap = getExistingXscpAssets(action);

      return details.map((detail) => {
        // Resolve element URLs while source asset IDs are still available,
        // then remap asset references inside component properties.
        const element = replaceAssets(assetMap)(detail.element);
        return {
          ...detail,
          element: optionToBool(getSharedLibraryPasteImport(action))
            ? rewriteSharedLibraryPasteReferences(assetMap, element)
            : element,
        };
      });
    }
  );
};

const getPendingXscpData = (pendingPaste: {xscpData: unknown}) =>
  pendingPaste.xscpData;
const eitherNone = either(constant(None));

export const getXscpDataWhenPastingCrossSite = (
  action: PasteAction
): Option<XscpData> =>
  eitherNone((operations: PasteElementDetail[]) => {
    const {type} = action;
    const {DesignerStore: designerState} = action.state;

    if (operations.length) {
      if (isPastingCrossSite(action)) {
        return getXscpData(
          (action as SimplePasteAction).payload.clipboardData,
          action
        );
      }

      if (type === XSCP_ASSET_IMPORT_SUCCEEDED) {
        return designerState.pendingPaste.map(getPendingXscpData);
      }
    }

    return None;
  })(getPasteDetailsBeforeCloning(action));

export const getXscpDataWhenPastingSerializedXscp = (
  action: PasteAction
): Option<XscpData> =>
  eitherNone((operations: PasteElementDetail[]) => {
    const {type} = action;
    const {DesignerStore: designerState} = action.state;

    if (operations.length) {
      if (isPastingSerializedXscp(action)) {
        return getXscpData(
          (action as SimplePasteAction).payload.clipboardData,
          action
        );
      }

      if (type === XSCP_ASSET_IMPORT_SUCCEEDED) {
        return designerState.pendingPaste.map(getPendingXscpData);
      }
    }

    return None;
  })(getPasteDetailsBeforeCloning(action));

export const getXscpDataForPasteNotification = (
  action: PasteAction
): Option<XscpData> => getXscpDataWhenPastingSerializedXscp(action);

const getXscpImportedFilesFromHistoryItem = (historyItem: HistoryItem) =>
  getXscpImportedFiles(historyItem.action);

export const getXscpAssetsRemovedByUndo = weakMemo(
  (historyStack: UndoRedoHistory) => {
    const item = getMostRecentHistoryItem(historyStack);
    if (!item) return emptyArray;
    return getXscpImportedFilesFromHistoryItem(item);
  }
);

export const getXscpAssetsAddedByRedo = weakMemo(
  (historyStack: UndoRedoHistory) => {
    const item = getUpcomingHistoryItem(historyStack);
    if (!item) return emptyArray;
    return getXscpImportedFilesFromHistoryItem(item);
  }
);
export const getXscpAssetsToRevive = weakMemo((action: PasteAction) =>
  getXscpAssets(action).flatMap<FileRecord>((asset) => {
    const existing = findByFileHash(asset)(action.state.AssetStore.assets);
    return existing && existing.markedAsDeleted ? [existing] : emptyArray;
  })
);

const noPendingPasteErr = noneToErr('There is no paste operation pending.');

const anchorRemovedErr = noneToErr(
  'The element you were trying to paste into was removed.'
);
const maybeGetElement = (
  state: {DesignerStore: DesignerStateType; UiNodeStore: UiNodeStateType},
  id: string
): Option<ExpressionElement> => {
  const root = Component.getRender(selectCurrentComponent(state));
  const maybeElement = Expressions.getElementById(root, id);
  return maybeElement ? Some(maybeElement) : None;
};

const getPendingCrossSitePasteDetailsBeforeCloning = (
  action: XscpAssetImportedSuccededAction
): Result<string, PasteElementDetail[]> =>
  noPendingPasteErr(action.state.DesignerStore.pendingPaste)
    .chain((pendingPaste) => {
      const {libraryImport, pasteDetails} = pendingPaste;
      if (!libraryImport) return Ok(pasteDetails);
      if (
        !getFeatureFlagOutsideOfReact(FEATURE_FLAGS.FAST_COPY) ||
        !canCompleteSharedLibraryPaste(action.state, libraryImport)
      ) {
        return Err('Could not complete the paste. Copy and paste again.');
      }
      // Revalidate against current stores using the original paste selection
      // and prepared IDs, so the asset wait cannot redirect the paste.
      return getEventualCrossSitePasteDetails(
        {
          ...action,
          state: {
            ...action.state,
            UiNodeStore: {
              ...action.state.UiNodeStore,
              ...libraryImport.selection,
            },
          },
        },
        {
          element: libraryImport.element,
          libraryImport,
          xscpData: pendingPaste.xscpData,
        }
      );
    })
    .chain(
      traverseResults(
        ({element, anchorId, position, anchorPath}: PasteElementDetail) =>
          anchorRemovedErr(maybeGetElement(action.state, anchorId)).map(() => {
            const importedAssets = action.payload.response.get('importedFiles');
            if (!importedAssets)
              return {element, anchorId, position, anchorPath};

            const existingAssets = getExistingXscpAssets(action);
            const mergedAssets = importedAssets.merge(existingAssets);

            // Resolve element URLs before remapping component property asset IDs,
            // matching the immediate paste path.
            const elementWithAssets = replaceAssets(mergedAssets)(element);
            return {
              element: optionToBool(getSharedLibraryPasteImport(action))
                ? rewriteSharedLibraryPasteReferences(
                    mergedAssets,
                    elementWithAssets
                  )
                : elementWithAssets,
              anchorId,
              position,
              anchorPath,
            };
          })
      )
    );

const isOptimizationExpression = matchExpression({
  Element: isOptimizationElement,
  _: constantFalse,
});

/**
 * Processes a paste action within the same site. It checks canvas focus, ongoing paste operations,
 * user permissions, locale restrictions, and cross-site pasting prohibitions.
 * Returns details for a valid paste operation or an error message for any violations.
 */
export const getPasteDetailsBeforeCloning = weakMemo(
  (action: PasteAction): Result<string, PasteElementDetail[]> => {
    const {type, state} = action;

    if (!selectIsCanvasFocused(state)) {
      return Err('The Canvas is not in focus.');
    }

    if (type === XSCP_ASSET_IMPORT_SUCCEEDED) {
      return getPendingCrossSitePasteDetailsBeforeCloning(action);
    }

    if (type === PASTE) {
      if (optionToBool(state.DesignerStore.pendingPaste)) {
        return Err('A paste operation is in progress.');
      }

      // Silently ignore paste operations in webApps workflow (Orion code mode)
      if (selectIsCodeModeWorkflow(state)) {
        return Ok([]);
      }

      // Block pasting regular elements while editing code component
      if (selectIsEditingCodeComponent(state)) {
        return Err(`You can't paste elements while editing a code component.`);
      }

      if (!selectDesignerAbility('canAddElementToCanvas')(state)) {
        if (!selectDesignerAbility('canEditLocalizedCanvasItem')(state)) {
          return Err('You can only paste elements in the primary locale.');
        }

        if (selectIsInOptimizeMode(state.OptimizationStore)) {
          return Err(`You can't paste elements in Optimize mode.`);
        }

        return Err(`You don't currently have permission to paste elements.`);
      }

      if (isPastingSerializedXscp(action)) {
        return getCrossSitePasteDetailsBeforeCloning(action).chain(
          (details: PasteElementDetail[]) => {
            const hasOptimizationExpression = details.some(({element}) =>
              Expressions.some(isOptimizationExpression)(
                Expressions.getElementData(element)
              )
            );
            return hasOptimizationExpression
              ? Err(
                  "This content can't be pasted as it is part of an optimization."
                )
              : Ok(details);
          }
        );
      }

      const isInPageBuildMode =
        selectDesignerAbility('isInPageBuildMode')(state);

      const isInCampaignBuildMode = selectDesignerAbility(
        'isInCampaignBuildMode'
      )(state);

      return getSameSitePasteDetailsBeforeCloning({
        state: {
          ...state,
          isInPageBuildMode,
          isInCampaignBuildMode,
          isCampaignSite: state.SiteDataStore?.kind === 'campaign',
          cmsRtfComponentIds: selectAllCmsRtfComponentIds(state),
        },
      });
    }

    // nothing to paste
    return Ok([]);
  }
);

export const getPasteDetails = weakMemo(
  (action: PasteAction): Result<string, PasteElementDetail[]> => {
    return getPasteDetailsBeforeCloning(action).map((details) => {
      if (
        isPastingSerializedXscp(action) ||
        action.type === XSCP_ASSET_IMPORT_SUCCEEDED
      ) {
        return details.map((detail: PasteElementDetail) => {
          const {clonedElement, idsState} = cloneElementWithIdMapping(
            detail.element,
            action.state.DesignerStore.components,
            generateGuid()
          );

          // Section links, including property overrides, must follow the same
          // ID mapping that cloned their targets.
          const updatedElement = updateXscpSectionLinkAfterCloning(
            clonedElement,
            idsState.idMap
          );

          return {...detail, element: updatedElement};
        });
      }

      return details.map((detail) => {
        const clipboardData = maybe<ClipboardData | null>(null)(
          identity<ClipboardData>
        )(action.state.ClipboardStore.map((c) => c));

        // this should never happen. at this point we should have ensured clipboard data exists
        if (!clipboardData) throw new Error('Clipboard data not found.');

        const focusedSymbolIdPathInPasteData =
          clipboardData.uiNodeState.focusedSymbolNativeIdPath;

        const sourcePageId = clipboardData.designerState.currentPageId;

        const currentFocusedSymbolIdPath =
          action.state.UiNodeStore.focusedSymbolNativeIdPath;
        const currentPageId = action.state.DesignerStore.currentPageId;

        const isPastingWithinSamePage = currentPageId === sourcePageId;

        // We want to delete bind  when pasting an element that originates
        // from inside a component instance into any location that is not
        // within the exact component instance the element was copied from.
        // This prevents bindings to component instance override fields from escaping outside
        // the component instance where those component instance override fields are available.
        const cloneWithoutBind: boolean =
          focusedSymbolIdPathInPasteData.length > 0 &&
          (!isPastingWithinSamePage ||
            !isEqual(
              focusedSymbolIdPathInPasteData,
              currentFocusedSymbolIdPath
            ));

        const cloned = cloneWithoutBind
          ? cloneElementWithoutComponentPropertyBindings(
              detail.element,
              clipboardData.designerState.components,
              clipboardData.designerState.plugins,
              focusedSymbolIdPathInPasteData,
              generateGuid()
            )
          : cloneElement(
              detail.element,
              clipboardData.designerState.components,
              generateGuid()
            );

        return {...detail, element: cloned};
      });
    });
  }
);

export const addElements = (
  action: PasteAction | DesignerAction<typeof CONTEXT_MENU_DUPLICATE>,
  pasteDetails: PasteElementDetail[]
): Option<Component.ComponentMap> => {
  const {
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
    StyleBlockStore: styleBlockState,
  } = action.state;

  return pasteDetails.reduce(
    (
      updatedComponentMap: Option<Component.ComponentMap>,
      pasteDetail: PasteElementDetail
    ): Option<Component.ComponentMap> => {
      return updatedComponentMap.chain((components) => {
        const updatedDesignerState = designerState.set(
          'components',
          components
        );

        // A paste onto projected slot-default content is the same placement a
        // drag makes, and only the projected mover can resolve an anchor that no
        // render holds. Hand it the anchor's native id path the way DRAG_STOP
        // does, so the insertion lands beside the anchor instead of promoting
        // the slot over the content already there.
        const anchorNativeIdPath = uiNodeState.selectedNativeIdPath;
        const slotDefaultContext = anchorNativeIdPath
          ? resolveSlotDefaultPathContext(
              anchorNativeIdPath,
              components,
              designerState.plugins,
              pasteDetail.anchorPath ?? null
            )
          : null;

        if (slotDefaultContext) {
          const placedDesignerState = applyProjectedPlacement(
            {
              source: {kind: 'new', element: pasteDetail.element},
              destination: {
                id: pasteDetail.anchorId,
                nativeIdPath: anchorNativeIdPath,
                path: pasteDetail.anchorPath ?? null,
                position: pasteDetail.position,
              },
            },
            {
              designerState: updatedDesignerState,
              styleBlockState,
              cssGridUiState: action.state.CssGridUiStore,
              externalState: action.state.ExternalStore,
              uiNodeState,
              shift: action.state.KeyboardStore.shift,
            }
          );
          return Some(placedDesignerState.components);
        }

        return addElement({...pasteDetail, styleBlockState})(
          updatedDesignerState,
          uiNodeState
        ).map((newComponentMap: Component.ComponentMap) => {
          const finalDesignerState = updatedDesignerState.set(
            'components',
            newComponentMap
          );

          return placeGridItem({
            id: pasteDetail.element.id,
            forceManualPlacement: action.state.KeyboardStore.shift,
            mediaQuery: action.state.ExternalStore.mediaQuery,
            pseudoMode: action.state.ExternalStore.pseudoMode,
            variantState: action.state.ExternalStore.variantState,
            cssGridUiState: action.state.CssGridUiStore,
            previousDesignerState: updatedDesignerState,
            nextDesignerState: finalDesignerState,
            styleBlockState,
            uiNodeState,
          });
        });
      });
    },
    Some(designerState.components)
  );
};

const paste = (action: PasteAction): Component.ComponentMap => {
  const {DesignerStore: designerState} = action.state;

  return maybe(designerState.components)<Component.ComponentMap>(identity)(
    okToOption(getPasteDetails(action))
      .map((details: PasteElementDetail[]) => {
        if (
          isPastingSerializedXscp(action) ||
          action.type === XSCP_ASSET_IMPORT_SUCCEEDED
        ) {
          return details.map((detail: PasteElementDetail) => {
            const xscpStyles = importXscpStyles(action).idMap;

            const updatedElement = Expressions.getElement(
              updateXscpStyleBlockIds(xscpStyles)(EElement(detail.element))
            );
            if (!updatedElement) return detail;

            return {...detail, element: updatedElement};
          });
        }

        return details;
      })
      .map(setElementStylesOnPaste)
      .map(removeFlowIdOnPaste)
      .chain((details: PasteElementDetail[]) => {
        if (details.length === 0) return Some(designerState.components);
        const components = maybe(designerState.components)(
          (libraryImport: XscpLibraryImport) =>
            designerState.components.union(libraryImport.components)
        )(getSharedLibraryPasteImport(action));
        return addElements(
          {
            ...action,
            state: {
              ...action.state,
              DesignerStore: designerState.set('components', components),
            },
          },
          details
        );
      })
      .map((updatedComponentMap: Component.ComponentMap) => {
        // Serialized paste payloads must not restore components from local clipboard state.
        // ClipboardStore may still describe an unrelated earlier same-site copy.
        if (
          isPastingSerializedXscp(action) ||
          action.type === XSCP_ASSET_IMPORT_SUCCEEDED
        )
          return updatedComponentMap;

        const clipboard = maybe<ClipboardData | null>(null)(
          identity<ClipboardData>
        )(action.state.ClipboardStore.map((c) => c));

        if (!clipboard) return updatedComponentMap;

        // the previous restoreUserComponentsOnPaste is used to validate the staged component map
        // we have to restore the user components here to actually update the component map
        return restoreUserComponentsOnPaste(
          clipboard.clippedElement,
          clipboard.designerState.components,
          updatedComponentMap
        );
      })
  );
};

const isRestrictedPasteEvent = (
  selectedElement: ExpressionElement | null,
  pastedElement: ExpressionElement | null
): boolean => {
  if (!(selectedElement && pastedElement)) return false;
  return layoutLib.isRestrictedLayoutPasteEvent(selectedElement, pastedElement)
    .restricted;
};

export const handlePaste = (
  action: PasteAction,
  componentMap: Component.ComponentMap
): Component.ComponentMap => {
  const selectedElement = selectSelectedElement(action.state);
  const pastedElement = getClippedElement(action.state);

  /**
   * Here we're handling the special case of attempting to paste Quick Stack Cell
   * elements into other Quick Stack Cell elements.
   */
  if (
    selectedElement &&
    isCell(selectedElement) &&
    pastedElement &&
    isCell(pastedElement)
  ) {
    const componentType = selectCurrentComponentType(action.state);
    return layoutLib.pasteCellInComponentMap({
      cellElementId: selectedElement.id,
      pastedElement,
      componentMap,
      componentType,
    });
  }

  if (isRestrictedPasteEvent(selectedElement, pastedElement)) {
    return componentMap;
  }

  return paste(action);
};

type RawParentAdjustmentResult =
  | {kind: 'adjust'; newRawParent: ExpressionElement}
  | {kind: 'error'; error: string};

function adjustedRawParentForSlotDefaultPaste(
  uiNodeState: UiNodeStateType,
  currentComponentRoot: Expression,
  anchorPath: ExpressionPath | null,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): RawParentAdjustmentResult | null {
  const {selectedNativeIdPath, focusedSymbolNativeIdPath} = uiNodeState;
  const slotDefaultContext = resolveSlotDefaultPathContext(
    selectedNativeIdPath,
    componentMap,
    plugins,
    anchorPath
  );
  // If the selected element is in the slot default value of a
  // component instance, the eventual paste will target the override
  // for that slot on that component instance.
  if (slotDefaultContext) {
    const relativePath = getRelativePath(
      slotDefaultContext.ownerInstancePath,
      focusedSymbolNativeIdPath
    );
    // targeted component is a component instance inside the currently
    // focused component
    if (relativePath.length === 1) {
      // find the targeted component instance
      const parentInstanceId = getGuidNullable(relativePath[0]);
      const parentInstance = parentInstanceId
        ? Expressions.getElementById(currentComponentRoot, parentInstanceId)
        : null;
      // set the targeted component instance as the rawParent for validation
      return parentInstance
        ? {kind: 'adjust', newRawParent: parentInstance}
        : {kind: 'error', error: 'Could not find component instance.'};
    }
  }

  return null;
}

const forTesting = {
  getPastePosition,
  updateXscpSectionLinkAfterCloning,
  replaceAssets,
  getCdnUrls,
  getXscpAssets,
  getExistingXscpAssets,
  getNewXscpAssets,
  isPastingNewCrossSiteAssets,
  shouldUseNativeXscpDataForSameSitePaste,
  isPastingSerializedXscp,
};

export {forTesting};
